import { i18n } from '../i18n'

/** Firebase Auth error code to i18n key mapping for friendly login toasts */
const FIREBASE_CODE_TO_KEY: Record<string, string> = {
  'auth/invalid-credential': 'auth.errorInvalidCredentials',
  'auth/invalid-email': 'auth.errorInvalidEmail',
  'auth/user-not-found': 'auth.errorInvalidCredentials',
  'auth/wrong-password': 'auth.errorInvalidCredentials',
  'auth/too-many-requests': 'auth.errorTooManyAttempts',
  'auth/popup-closed-by-user': 'auth.errorLoginCancelled',
  'auth/cancelled-popup-request': 'auth.errorLoginCancelled',
  'auth/account-exists-with-different-credential': 'auth.errorAccountExistsWithDifferentCredential',
  'auth/operation-not-allowed': 'auth.errorProviderDisabled',
  'auth/network-request-failed': 'auth.errorNetwork',
  'auth/popup-blocked': 'auth.errorPopupBlocked',
  'auth/unauthorized-domain': 'auth.errorUnauthorizedDomain',
  'auth/internal': 'auth.errorProviderDisabled',
  'auth/credential-already-in-use': 'auth.errorAccountExistsWithDifferentCredential',
}

/** Firebase Auth error codes for password reset (sendPasswordResetEmail) */
const FIREBASE_RESET_PASSWORD_CODE_TO_KEY: Record<string, string> = {
  'auth/user-not-found': 'auth.errorResetUserNotFound',
  'auth/invalid-email': 'auth.errorInvalidEmail',
  'auth/too-many-requests': 'auth.errorTooManyAttempts',
  'auth/network-request-failed': 'auth.errorNetwork',
}

/** Firebase Auth error codes for confirmPasswordReset / action handler */
const FIREBASE_CONFIRM_RESET_CODE_TO_KEY: Record<string, string> = {
  'auth/invalid-action-code': 'auth.errorInvalidActionCode',
  'auth/expired-action-code': 'auth.errorExpiredActionCode',
  'auth/weak-password': 'auth.errorWeakPassword',
  'auth/network-request-failed': 'auth.errorNetwork',
}

/** Firebase Auth error codes for register (sign-up) */
const FIREBASE_REGISTER_CODE_TO_KEY: Record<string, string> = {
  'auth/email-already-in-use': 'auth.errorEmailAlreadyInUse',
  'auth/invalid-email': 'auth.errorInvalidEmail',
  'auth/weak-password': 'auth.errorWeakPassword',
  'auth/operation-not-allowed': 'auth.errorProviderDisabled',
  'auth/network-request-failed': 'auth.errorNetwork',
}

function getErrorCode(err: unknown): string | undefined {
  const e = err as { code?: string; response?: { data?: { code?: string } } }
  return e?.code ?? e?.response?.data?.code
}

/**
 * Returns a user-friendly, localized message for login errors (Firebase or generic).
 */
export function getFriendlyLoginErrorMessage(err: unknown): string {
  const code = getErrorCode(err)
  const key = code && FIREBASE_CODE_TO_KEY[code]
  if (key) {
    const msg = i18n.translate(key)
    if (msg && msg !== key) return msg
  }
  return i18n.translate('auth.loginFailed')
}

/**
 * Returns a user-friendly, localized message for register errors (Firebase or generic).
 */
export function getFriendlyRegisterErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code
  const key = code && FIREBASE_REGISTER_CODE_TO_KEY[code]
  if (key) {
    const msg = i18n.translate(key)
    if (msg && msg !== key) return msg
  }
  return i18n.translate('auth.registerFailed')
}

/**
 * Returns a user-friendly, localized message for password reset errors (Firebase).
 */
export function getFriendlyResetPasswordErrorMessage(err: unknown): string {
  const code = getErrorCode(err)
  const key = code && FIREBASE_RESET_PASSWORD_CODE_TO_KEY[code]
  if (key) {
    const msg = i18n.translate(key)
    if (msg && msg !== key) return msg
  }
  return i18n.translate('auth.resetPasswordFailed')
}

/**
 * Returns a user-friendly message for confirmPasswordReset / action handler errors.
 */
export function getFriendlyConfirmResetErrorMessage(err: unknown): string {
  const code = getErrorCode(err)
  const key = code && FIREBASE_CONFIRM_RESET_CODE_TO_KEY[code]
  if (key) {
    const msg = i18n.translate(key)
    if (msg && msg !== key) return msg
  }
  return i18n.translate('auth.resetPasswordFailed')
}
